import { z } from 'zod/v4';

import type { MigrationContext } from './types';

/**
 * A pre-versioning ("legacy") shape of an entity. One entity may have several legacy
 * variants converging into the same unified shape (e.g. the websites and mini-landing
 * forms of the same widget). Variants are tried in declaration order and the first
 * whose schema matches wins — keep schemas mutually discriminating.
 */
export type LegacyEntityVariantDef<TSchema extends z.ZodType = z.ZodType> = {
    /** Identifies the variant in error messages, e.g. 'web', 'ml'. */
    name: string;
    /** Identification schema for this legacy form. */
    schema: TSchema;
};

export type LegacyEntitySchema<T> = {
    safeParse: (input: unknown, ctx?: MigrationContext) => { type: 'ok'; value: T } | { type: 'err'; error: unknown };
    /** Combined schema of all variants — e.g. for validateStandardSchema in signal forms. */
    latestSchema: z.ZodType<T>;
    variants: readonly LegacyEntityVariantDef[];
};

/**
 * Creates a legacy-only entity schema: the entity exists only in its pre-versioning
 * persisted shape(s); parsing identifies the matching variant and passes the data
 * through unchanged. When a redesigned `default/v0` lands, the entity switches to a
 * versioned schema and the legacy variants gain `up` migrations into it.
 */
export const createLegacyEntitySchema = <V extends readonly [LegacyEntityVariantDef, ...LegacyEntityVariantDef[]]>(
    variants: V
): LegacyEntitySchema<z.output<V[number]['schema']>> => {
    type Value = z.output<V[number]['schema']>;

    const latestSchema = (
        variants.length === 1 ? variants[0].schema : z.union(variants.map((variant) => variant.schema))
    ) as z.ZodType<Value>;

    const safeParse = (input: unknown): { type: 'ok'; value: Value } | { type: 'err'; error: unknown } => {
        let lastError: unknown = null;

        for (const variant of variants) {
            const parsed = variant.schema.safeParse(input);
            if (parsed.success) {
                return { type: 'ok', value: parsed.data as Value };
            }
            lastError = parsed.error;
        }

        // single variant: surface its ZodError with precise issue paths
        if (variants.length === 1) {
            return { type: 'err', error: lastError };
        }

        const tried = variants.map((variant) => variant.name).join(', ');
        return { type: 'err', error: `no legacy variant matched (tried: ${tried})` };
    };

    return { safeParse, latestSchema, variants };
};
